import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useState } from 'react';

import { Button } from '@/components/ui/button';

interface StripePaymentFormProps {
  amount: number; // Amount in pence
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * Stripe payment form using PaymentElement.
 * Must be wrapped in an Elements provider with a client secret.
 * Balance updates are handled by Stripe webhooks.
 */
export function StripePaymentForm({ amount, onSuccess, onCancel }: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message ?? 'An error occurred during payment');
      setIsProcessing(false);
    } else {
      // Payment succeeded - webhook will update balance
      onSuccess();
    }
  };

  const formatAmount = (pence: number) => {
    return `£${(pence / 100).toFixed(2)}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never',
          },
          paymentMethodOrder: ['card'],
        }}
      />

      {errorMessage && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{errorMessage}</div>
      )}

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
          className="h-12 flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="h-12 flex-1 bg-[#131313] text-white hover:bg-[#131313]/90"
        >
          {isProcessing ? 'Processing...' : `Pay ${formatAmount(amount)}`}
        </Button>
      </div>
    </form>
  );
}
